import React, { useState } from 'react';
import { 
  BarChart3, Download, Calendar, Filter, TrendingUp, Package,
  DollarSign, Truck, Users, ArrowUp, ArrowDown, FileText,
  Eye, RefreshCw, Clock, CheckCircle
} from 'lucide-react';

const WarehouseReports = () => {
  const [dateRange, setDateRange] = useState('30d');
  const [reportType, setReportType] = useState('inventory');

  const reports = [
    {
      id: '1',
      name: 'Inventory Movement Report',
      description: 'Track all inventory movements including receipts and shipments',
      type: 'inventory',
      lastGenerated: '2024-02-28',
      status: 'ready',
      size: '2.4 MB'
    },
    {
      id: '2',
      name: 'Shipping Performance Report',
      description: 'Analysis of shipping times, costs, and delivery performance',
      type: 'shipping',
      lastGenerated: '2024-02-27',
      status: 'ready',
      size: '1.8 MB'
    },
    {
      id: '3',
      name: 'Stock Level Analysis',
      description: 'Current stock levels, low stock alerts, and reorder recommendations',
      type: 'inventory',
      lastGenerated: '2024-02-26',
      status: 'generating',
      size: 'Pending'
    },
    {
      id: '4',
      name: 'Warehouse Efficiency Report',
      description: 'Productivity metrics, space utilization, and operational efficiency',
      type: 'operations',
      lastGenerated: '2024-02-25',
      status: 'ready',
      size: '3.1 MB'
    },
    {
      id: '5',
      name: 'Cost Analysis Report',
      description: 'Breakdown of warehouse costs including labor, storage, and shipping',
      type: 'financial',
      lastGenerated: '2024-02-24',
      status: 'ready',
      size: '1.5 MB'
    }
  ];

  const metrics = [
    {
      title: 'Total Items Processed',
      value: '15,234',
      change: '+12.5%',
      trend: 'up',
      icon: Package,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      title: 'Shipping Accuracy',
      value: '99.2%',
      change: '+0.3%',
      trend: 'up',
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100'
    },
    {
      title: 'Average Processing Time',
      value: '2.4 hrs',
      change: '-15%',
      trend: 'down',
      icon: Clock,
      color: 'text-purple-600 bg-purple-100'
    },
    {
      title: 'Cost per Shipment',
      value: '$12.45',
      change: '-8.2%',
      trend: 'down',
      icon: DollarSign,
      color: 'text-orange-600 bg-orange-100'
    }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Ready
        </span>;
      case 'generating':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
          Generating
        </span>;
      case 'error':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <X className="h-3 w-3 mr-1" />
          Error
        </span>;
      default:
        return null;
    }
  };

  const filteredReports = reportType === 'all' 
    ? reports 
    : reports.filter(report => report.type === reportType);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Warehouse Reports</h1>
              <p className="text-gray-600">Generate and download warehouse analytics reports</p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <button className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Generate Report</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{metric.value}</p>
                  <div className={`flex items-center mt-2 text-sm ${
                    metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.trend === 'up' ? (
                      <ArrowUp className="h-4 w-4 mr-1" />
                    ) : (
                      <ArrowDown className="h-4 w-4 mr-1" />
                    )}
                    <span>{metric.change}</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${metric.color}`}>
                  <metric.icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Reports</option>
                <option value="inventory">Inventory Reports</option>
                <option value="shipping">Shipping Reports</option>
                <option value="operations">Operations Reports</option>
                <option value="financial">Financial Reports</option>
              </select>
            </div>
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Available Reports ({filteredReports.length})</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Generated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{report.name}</div>
                        <div className="text-sm text-gray-500">{report.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                        {report.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {report.lastGenerated}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(report.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {report.size}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button 
                          className="text-gray-400 hover:text-gray-600"
                          title="View Report"
                          disabled={report.status !== 'ready'}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button 
                          className="text-gray-400 hover:text-gray-600"
                          title="Download Report"
                          disabled={report.status !== 'ready'}
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button 
                          className="text-gray-400 hover:text-gray-600"
                          title="Regenerate Report"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Report Generation */}
        <div className="mt-8 bg-orange-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Report Generation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="bg-white border border-orange-200 text-orange-700 py-3 px-4 rounded-lg hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2">
              <Package className="h-4 w-4" />
              <span>Inventory Summary</span>
            </button>
            <button className="bg-white border border-orange-200 text-orange-700 py-3 px-4 rounded-lg hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2">
              <Truck className="h-4 w-4" />
              <span>Shipping Report</span>
            </button>
            <button className="bg-white border border-orange-200 text-orange-700 py-3 px-4 rounded-lg hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2">
              <TrendingUp className="h-4 w-4" />
              <span>Performance Report</span>
            </button>
            <button className="bg-white border border-orange-200 text-orange-700 py-3 px-4 rounded-lg hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2">
              <DollarSign className="h-4 w-4" />
              <span>Cost Analysis</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarehouseReports;